import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  TextInput,
  Image,
  Platform,
  Dimensions,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { navigate as navigateTo } from '../../navigation/NavigationService';
import UserMenu from './UserMenu';
import InviteModal from './InviteModal';
import { invitesAPI } from '../../api/invites';

interface AppToolbarProps {
  title?: string;
  showBackButton?: boolean;
  showSearch?: boolean;
  showSettings?: boolean;
  showProfile?: boolean;
  showShare?: boolean;
  showTabs?: boolean;
  activeTab?: string;
  onTabPress?: (tab: string) => void;
  onBackPress?: () => void;
  rightButton?: {
    icon: string;
    onPress: () => void;
  };
  /** Use lighter highlight-based background (e.g. for Mood Board) instead of dark gradient */
  headerVariant?: 'default' | 'highlight';
}

const AppToolbar: React.FC<AppToolbarProps> = ({
  title,
  showBackButton = false,
  showSearch = false,
  showSettings: _showSettings = true,
  showProfile = true,
  showShare: _showShare = true,
  showTabs = false,
  activeTab,
  onTabPress,
  onBackPress,
  rightButton,
  headerVariant: _headerVariant = 'default', // reserved for future header variants
}) => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { colors } = useTheme();
  const { user, signOut, isAuthenticated } = useAuth();
  const [searchText, setSearchText] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const screenWidth = Dimensions.get('window').width;
  const isWebLayout = Platform.OS === 'web' && screenWidth >= 768;

  // Generate invite code based on user ID
  const inviteCode = useMemo(() => {
    if (!user?.id) return '';
    // Simple hash-based code generation
    const generateCode = (input: string): string => {
      let hash = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      // Convert to base36 and pad with user ID
      const hashStr = Math.abs(hash).toString(36).toUpperCase();
      const userIdStr = user.id.toString().padStart(4, '0');
      // Combine and take first 12 characters
      return (hashStr + userIdStr).substring(0, 12);
    };
    const input = `invite_${user.id}_${user.email || 'user'}`;
    return generateCode(input);
  }, [user?.id, user?.email]);

  // Generate invite link
  const inviteLink = useMemo(() => {
    if (!inviteCode) return '';
    const baseUrl =
      Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://mycoloursense.com';
    return `${baseUrl}/invite/${inviteCode}`;
  }, [inviteCode]);

  const navItems = [
    { key: 'HomeTab', label: 'Home', icon: 'home-outline' },
    { key: 'MoodBoardTab', label: 'Mood Boards', icon: 'grid-outline' },
  ];

  const handleBackPress = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      navigation.goBack();
    }
  };

  const handleSettingsPress = () => {
    navigation.navigate('Settings');
  };

  const handleProfilePress = () => {
    // Navigate to Profile tab
    if (onTabPress) {
      onTabPress('ProfileTab');
    } else {
      // For mobile, navigate directly to ProfileTabMobile
      if (!isWebLayout) {
        try {
          (
            navigation as NativeStackNavigationProp<RootStackParamList>
          ).navigate('ProfileTabMobile' as never);
        } catch {
          navigateTo('MainTabs', { initialTab: 'ProfileTab' });
        }
      } else {
        navigateTo('MainTabs', { initialTab: 'ProfileTab' });
      }
    }
  };

  const handleLogoPress = () => {
    if (isAuthenticated) {
      navigateTo('MainTabs', { section: 'home' });
    } else {
      navigation.navigate('Landing');
    }
  };

  const handleTabPress = (tabKey: string) => {
    if (onTabPress) {
      onTabPress(tabKey);
    }
  };

  const handleInvitePress = async () => {
    if (!inviteLink || !inviteCode) {
      Alert.alert('Error', 'Invite link is not available. Please try again.');
      return;
    }

    // Save invite code to backend if it doesn't exist
    try {
      await invitesAPI.create(inviteCode);
    } catch (error: unknown) {
      // If invite already exists, that's fine - just continue
      const err = error as Error;
      if (!err.message?.includes('already exists')) {
        // Continue anyway - the invite code will still work
      }
    }

    setShowInviteModal(true);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch {
      // Logout failed silently
    }
  };

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.background,
          borderBottomColor: colors.border,
          ...(Platform.OS === 'web'
            ? {
                boxShadow: `0px 4px 12px 0px ${colors.cardText}1A`,
              }
            : { shadowColor: colors.cardText }),
        },
      ]}
    >
      <SafeAreaView style={styles.safeArea}>
        <View style={[isWebLayout ? styles.content : styles.contentMobile]}>
          {/* Left Section */}
          <View style={styles.leftSection}>
            {showBackButton ? (
              <TouchableOpacity
                style={styles.backButton}
                onPress={handleBackPress}
              >
                <Ionicons name="chevron-back" size={24} color={colors.text} />
              </TouchableOpacity>
            ) : (
              <TouchableOpacity onPress={handleLogoPress}>
                {/* On mobile always use icon; on web use large logo for FAQ, Privacy, Terms, Blog, Article */}
                {isWebLayout &&
                title &&
                (title === 'Frequently Asked Questions' ||
                  title === 'Privacy Policy' ||
                  title === 'Terms of Service' ||
                  title === 'Blog' ||
                  title === 'Article') ? (
                  <Image
                    source={require('../../../assets/coloursense_logo_with_name.png')}
                    style={styles.logoLarge}
                    resizeMode="contain"
                    fadeDuration={0}
                  />
                ) : (
                  <Image
                    source={require('../../../assets/icon.png')}
                    style={styles.logoIcon}
                    resizeMode="contain"
                    fadeDuration={0}
                  />
                )}
              </TouchableOpacity>
            )}
          </View>

          {/* Center Section: Title, Search Bar, or Navigation Tabs */}
          <View style={styles.centerSection}>
            {/* Show title when present */}
            {title && (
              <Text
                style={[styles.title, { color: colors.text }]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {title}
              </Text>
            )}

            {/* Show search bar when enabled and web layout (only if no title) */}
            {!title && showSearch && isWebLayout && (
              <View
                style={[
                  styles.searchContainer,
                  {
                    backgroundColor: colors.border,
                    borderColor: colors.border,
                  },
                ]}
              >
                <Ionicons
                  name="search-outline"
                  size={18}
                  color={colors.secondary}
                  style={styles.searchIcon}
                />
                <TextInput
                  style={[styles.searchInput, { color: colors.text }]}
                  placeholder="Search..."
                  placeholderTextColor={colors.secondary}
                  value={searchText}
                  onChangeText={setSearchText}
                />
              </View>
            )}

            {/* Show navigation tabs when enabled and web layout (only if no title) */}
            {!title && showTabs && isWebLayout && (
              <View style={styles.navItemsContainer}>
                {navItems.map((item) => (
                  <TouchableOpacity
                    key={item.key}
                    style={[
                      styles.navItem,
                      activeTab === item.key && styles.navItemActive,
                    ]}
                    onPress={() => handleTabPress(item.key)}
                  >
                    <Ionicons
                      name={
                        item.icon as React.ComponentProps<
                          typeof Ionicons
                        >['name']
                      }
                      size={18}
                      color={
                        activeTab === item.key
                          ? colors.primary
                          : colors.secondary
                      }
                    />
                    <Text
                      style={[
                        styles.navLabel,
                        {
                          color:
                            activeTab === item.key
                              ? colors.primary
                              : colors.secondary,
                        },
                        activeTab === item.key && styles.navLabelActive,
                      ]}
                    >
                      {item.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Right Section */}
          <View style={styles.rightSection}>
            {rightButton && (
              <TouchableOpacity
                style={styles.iconButton}
                onPress={rightButton.onPress}
              >
                <Ionicons
                  name={
                    rightButton.icon as React.ComponentProps<
                      typeof Ionicons
                    >['name']
                  }
                  size={24}
                  color={colors.primary}
                />
              </TouchableOpacity>
            )}

            {/* Profile menu (avatar → View profile, Settings, Invite, Logout) */}
            {showProfile && user && (
              <UserMenu
                onProfilePress={handleProfilePress}
                onSettingsPress={handleSettingsPress}
                onInvitePress={handleInvitePress}
                onLogout={handleLogout}
              />
            )}
          </View>
        </View>
      </SafeAreaView>

      {/* Invite Modal */}
      <InviteModal
        visible={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        inviteLink={inviteLink}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 1000,
    borderBottomWidth: 1,
  },
  safeArea: {
    flex: 1,
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 50,
    paddingVertical: 8,
    minHeight: 48,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-start',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  logo: {
    width: 180,
    height: 45,
  },
  logoTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  logoIcon: {
    width: 30,
    height: 30,
  },
  logoLarge: {
    width: 180,
    height: 45,
  },
  logoLargeMobile: {
    width: 100,
    height: 25,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
  },
  titleMobile: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderRadius: 8,
    width: 200,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    padding: 0,
  },
  centerSection: {
    flex: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navItemsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 12,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginHorizontal: 2,
    borderRadius: 8,
  },
  navItemActive: {
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
  },
  navLabel: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '500',
  },
  navLabelActive: {
    fontWeight: '600',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  iconButton: {
    padding: 8,
    borderRadius: 8,
  },
  profileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
  },
  profileText: {
    marginRight: 12,
    alignItems: 'flex-end',
  },
  greeting: {
    fontSize: 12,
    marginBottom: 2,
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
  },

  // mobile
  contentMobile: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 25,
    paddingVertical: 8,
    minHeight: 48,
  },
  logoMobile: {
    width: 140,
    height: 36,
  },
});

export default AppToolbar;
